/**
 * GA4 theme events for Shopify Custom Pixel + GTM
 *
 * Tracks:
 * - select_item   → product card link clicks
 * - remove_from_cart → cart / cart-drawer remove button clicks
 *
 * IMPORTANT (Shopify Custom Pixel):
 * Theme-side window.dataLayer is NOT the same as the sandboxed pixel dataLayer.
 * These events are published with Shopify.analytics.publish() so your Custom Pixel
 * can subscribe and forward them into GTM. Also pushes to window.dataLayer for
 * debugging / any main-frame GTM.
 *
 * Add these subscribers to your GTM Custom Pixel (Customer events → Custom pixel):
 *
 *   analytics.subscribe('select_item', (event) => {
 *     window.dataLayer.push({ ecommerce: null });
 *     window.dataLayer.push({
 *       event: 'select_item',
 *       ecommerce: event.customData?.ecommerce,
 *     });
 *   });
 *
 *   analytics.subscribe('remove_from_cart', (event) => {
 *     window.dataLayer.push({ ecommerce: null });
 *     window.dataLayer.push({
 *       event: 'remove_from_cart',
 *       ecommerce: event.customData?.ecommerce,
 *     });
 *   });
 *
 * Theme selectors used (already wired with data-ga4-item attributes):
 * - select_item:      product-card[data-ga4-item] a[href]
 * - remove_from_cart: .cart-items__remove-button[data-ga4-item]
 */
(() => {
  const CURRENCY =
    (window.FoxTheme && window.FoxTheme.currency) ||
    (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
    '';

  const centsToUnit = (cents) => {
    const value = Number(cents);
    if (Number.isNaN(value)) return 0;
    return Number((value / 100).toFixed(2));
  };

  const buildItemFromEl = (el, overrides = {}) => {
    if (!el) return null;

    const price = centsToUnit(el.dataset.itemPrice);
    const quantity = Number(el.dataset.itemQuantity || 1);

    return {
      item_id: String(el.dataset.itemId || el.dataset.itemSku || ''),
      item_name: el.dataset.itemName || '',
      item_brand: el.dataset.itemBrand || undefined,
      item_category: el.dataset.itemCategory || undefined,
      item_variant: el.dataset.itemVariant || undefined,
      price,
      quantity,
      ...overrides,
    };
  };

  const pushGa4Event = (eventName, ecommerce) => {
    window.dataLayer = window.dataLayer || [];
    // Clear previous ecommerce object (GA4 recommendation)
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: eventName,
      ecommerce,
    });

    // Bridge into Shopify Custom Pixel sandbox → GTM
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(eventName, { ecommerce });
    }
  };

  const getItemListMeta = (card) => {
    const listName =
      card.dataset.itemListName ||
      document.querySelector('[data-ga4-item-list-name]')?.dataset.ga4ItemListName ||
      document.title ||
      '';

    const listRoot =
      card.closest('.swiper-wrapper, .swipe__inner, .product-grid, [data-ga4-item-list]') || card.parentElement;
    const siblings = listRoot
      ? Array.from(listRoot.querySelectorAll('product-card[data-ga4-item]'))
      : [];
    const index = siblings.length ? siblings.indexOf(card) + 1 : Number(card.dataset.itemIndex || 0);

    return {
      item_list_id: card.dataset.itemListId || undefined,
      item_list_name: listName || undefined,
      index: index > 0 ? index : undefined,
    };
  };

  const onSelectItem = (event) => {
    const card = event.target.closest('product-card[data-ga4-item]');
    if (!card) return;

    // Ignore non-navigation controls on the card (quick-add, swatches, etc.)
    if (event.target.closest('quick-add-component, product-form-component, .quick-add, button, [type="submit"]')) {
      return;
    }

    const link = event.target.closest('a[href]');
    if (!link || !card.contains(link)) return;
    // Skip cart / account / hash links mistakenly nested
    if (link.getAttribute('href')?.startsWith('#')) return;

    const listMeta = getItemListMeta(card);
    const item = buildItemFromEl(card, {
      index: listMeta.index,
      item_list_id: listMeta.item_list_id,
      item_list_name: listMeta.item_list_name,
    });
    if (!item?.item_id && !item?.item_name) return;

    pushGa4Event('select_item', {
      currency: card.dataset.itemCurrency || CURRENCY,
      item_list_id: listMeta.item_list_id,
      item_list_name: listMeta.item_list_name,
      items: [item],
    });
  };

  const onRemoveFromCart = (event) => {
    const button = event.target.closest('.cart-items__remove-button[data-ga4-item]');
    if (!button) return;

    const item = buildItemFromEl(button);
    if (!item?.item_id && !item?.item_name) return;

    const value = Number((item.price * item.quantity).toFixed(2));

    pushGa4Event('remove_from_cart', {
      currency: button.dataset.itemCurrency || CURRENCY,
      value,
      items: [item],
    });
  };

  document.addEventListener('click', onSelectItem, true);
  document.addEventListener('click', onRemoveFromCart, true);
})();
